import { useContext } from 'react';
import { LanguageContext } from '@/contexts/LanguageContext';

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }

  const { language, setLanguage, translations } = context;

  /**
   * Translate a key with optional variable interpolation
   * @param key - Translation key (e.g., 'hero.title' or 'common.save')
   * @param variables - Optional object with variables to interpolate (e.g., { name: 'John' })
   * @returns Translated string or the key itself if translation not found
   */
  const t = (key: string, variables?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let value: any = translations;

    // Navigate through nested keys
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Translation not found, return the key itself for debugging
        console.warn(`Translation missing for key: ${key} in language: ${language}`, { translations, keys, currentValue: value });
        return key;
      }
    }

    // If value is not a string, return the key
    if (typeof value !== 'string') {
      console.warn(`Translation for key ${key} is not a string`, { value, type: typeof value });
      return key;
    }

    // Interpolate variables if provided
    if (variables) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        return variables[varName]?.toString() || match;
      });
    }

    return value;
  };

  return {
    t,
    language,
    setLanguage,
  };
};
